import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { hideMenu } from '../utils';
import { useLoading } from '../contexts/LoadingContext';
import { useLanguage } from '../LanguageContext';
import { useTranslate } from '../services/translation';
import { getProductBySlug, PortfolioProduct, getTranslationByKey, TranslationContent} from '../api/API';
import ProductDetailSkeleton from '../components/ProductDetailSkeleton';
import '../assets/css/ProductDetail.css';

interface ProductContent {
  html: string;
  style: string;
}

const extractStyleFromHtml = (html: string): ProductContent => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const styleTag = doc.querySelector('style');
  const style = styleTag?.textContent || '';

  if (styleTag) {
    styleTag.remove();
  }

  return {
    html: doc.body.innerHTML,
    style
  };
};

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { translate } = useTranslate();
  const { startLoading, stopLoading } = useLoading();
  const [product, setProduct] = useState<PortfolioProduct | null>(null);
  const [contentData, setContentData] = useState<TranslationContent[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [productStyle, setProductStyle] = useState<string>('');
  const [cleanHtml, setCleanHtml] = useState<string>('');

  useEffect(() => {
    hideMenu();
    if (!slug) return;

    const loadProductDetail = async () => {
      setIsLoading(true);
      startLoading();

      try {
        const productResponse = await getProductBySlug(slug);
        if (productResponse && productResponse.data) {
          setProduct(productResponse.data);

          // Récupérer le contenu traduit
          const contentResponse = await getTranslationByKey(productResponse.data.details!);
          if (contentResponse && contentResponse.data) {
            setContentData(contentResponse.data.contents);
          }

          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError(true);
      } finally {
        setIsLoading(false);
        stopLoading();
      }
    };

    loadProductDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (contentData) {
      const content = contentData.find((c) => c.lang === language);
      if (content) {
        const { html, style } = extractStyleFromHtml(content.value);
        console.log("style: ", style)
        setProductStyle(style);
        setCleanHtml(html);
      }
    }
  }, [contentData, language]);

  useEffect(() => {
    if (!productStyle) {
      return;
    }

    const styleElement = document.createElement('style');

    styleElement.setAttribute(
      'data-product-style',
      'true'
    );

    styleElement.innerHTML = productStyle;

    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [productStyle]);

  if (isLoading) {
    return <ProductDetailSkeleton />;
  }

  if (error || !product) {
    return (
      <div className="product-detail-error">
        <h1 className="white">{translate('common.product')}</h1>
        <p className="light">{translate('common.no_product_found')}</p>
        <button
          className="btn-back"
          onClick={() => navigate(`/${language}/products`)}
        >
          ← {translate('common.view_more_products')}
        </button>
      </div>
    );
  }

  return (
    <div className="product-detail-page width-100p">
      <Helmet>
        <meta charSet="utf-8" name="description" content={translate(product.description as any)} />
        <title>{translate(product.title as any)} – Gence Lucien</title>
        <link rel="canonical" href={`https://gence.bemanjary.com/${language}/product/${product.slug}`} />
        <meta property="og:title" content={translate(product.title as any)} />
        <meta property="og:description" content={translate(product.description as any)} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="product-detail-container">
        <button
          className="btn-back-link"
          onClick={() => navigate(`/${language}/products`)}
        >
          ← {translate('common.view_more_products')}
        </button>

        <article className="product-detail-article">
          {contentData && (
            <div className="product-detail-content light">
              {cleanHtml ? (
                <div dangerouslySetInnerHTML={{ __html: cleanHtml }} />
              ) : (
                <p>{translate('common.content_not_available')}</p>
              )}
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default ProductDetail;